import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { internal_backend } from '~/internal-backend'
import { ModelListRowModel } from '../types'
import { GET_SHOPIFY_PRODUCTS_QUERY_KEY } from '~/features/shopify/hooks/useGetShopifyProducts'
import { UpdateVariantsParams } from '../../../../../shared/schemas/shopify'
import * as R from 'remeda'

export const useUpdateShopifyVariants = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: ['update-shopify-variants'],
    mutationFn: async (selectedRows: ModelListRowModel[]) => {
      // Filter out rows without Shopify products
      const rowsWithShopify = selectedRows.filter((row) => !!row.shopifyProduct)

      if (rowsWithShopify.length === 0) {
        throw new Error('No models with Shopify products selected')
      }

      // Group by productId since we need to update variants per product
      const updatesByProduct = R.pipe(
        rowsWithShopify,
        R.mapToObj((row) => [row.shopifyProduct!.id, row])
      )

      // Prepare update params for each product
      const updatePromises = Object.entries(updatesByProduct).map(([productId, row]) => {
        // Get the first row to extract the price (all rows for same product should have same price in models_v2)
        const modelPrice = row.price

        // Update all variants of this product with the model price
        const variants = row.shopifyProduct!.variants.nodes.map((variant) => ({
          id: variant.id,
          price: modelPrice,
          compareAtPrice: variant.price,
        }))

        const params: UpdateVariantsParams = {
          productId,
          variants,
        }

        return internal_backend.update_variants.request(params)
      })

      const results = await Promise.allSettled(updatePromises)

      const failures = results.filter((r) => r.status === 'rejected')
      if (failures.length > 0) {
        console.error('Failed to update some products:', failures)
        throw new Error(`Failed to update ${failures.length} out of ${results.length} products`)
      }

      return results
    },
    onSuccess: (data, selectedRows) => {
      const rowsWithShopify = selectedRows.filter((row) => row.shopifyProduct)
      toast.success(`Successfully updated prices for ${rowsWithShopify.length} Shopify products`)
      queryClient.invalidateQueries({ queryKey: [GET_SHOPIFY_PRODUCTS_QUERY_KEY] })
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to update Shopify prices')
    },
  })
}
